import Vue from 'vue'
import { mapGetters } from 'vuex'
import NumberColumn from '@/components/Columns/NumberColumn.vue'
import DurationColumn from '@/components/Columns/DurationColumn.vue'

import type { GridColumn } from '@/@types'
import type {
  ScriptColumnInitObject,
  ScriptInstance,
  ScriptUnit,
  ScriptTest,
} from '@/models/scripts'
import type { CardVariant } from '@/constants/scripts'

interface Methods {
  getComponentForThis: (column: GridColumn) => string
  getInitObjectForThis: (
    column: GridColumn,
    type: CardVariant,
  ) => ScriptColumnInitObject
  editTest: (type: CardVariant) => void
  removeTest: (type: CardVariant) => void
}

interface Computed {
  getScriptColumns: GridColumn[]
  statColumns: GridColumn[]
  columnsWidth: string
  greatestConversion: string
}

interface Props {
  item: ScriptUnit
  test: ScriptTest
}

export default Vue.extend<{}, Methods, Computed, Props>({
  components: {
    DurationColumn,
    NumberColumn,
  },
  props: {
    item: {
      required: true,
    },
    test: {
      required: true,
    },
  },
  computed: {
    ...mapGetters(['getScriptColumns']),
    statColumns(): GridColumn[] {
      return this.getScriptColumns.filter((one: GridColumn) => one.test)
    },
    columnsWidth(): string {
      return [
        '2rem',
        '1fr',
        '8rem',
        ...this.statColumns.map((one: GridColumn) => one.width),
        '14.5rem',
      ].join(' ')
    },
    greatestConversion(): string {
      return this.test?.stats?.a?.pass > 0 && this.test?.stats?.b?.pass > 0
        ? new Intl.NumberFormat('ru-RU', {
            maximumFractionDigits: 2,
          }).format(
            Math.max(
              (this.test.stats.a.target / this.test.stats.a.pass) * 100,
              (this.test.stats.b.target / this.test.stats.b.pass) * 100,
            ),
          ) + '%'
        : '0%'
    },
  },
  methods: {
    getComponentForThis(column: GridColumn): string {
      return column.type + 'Column'
    },
    getInitObjectForThis(
      column: GridColumn,
      type: CardVariant,
    ): ScriptColumnInitObject {
      const init: ScriptColumnInitObject = {
        id: this.item.id,
        title: this.item.title,
        value: null,
      }
      const instance: ScriptInstance = this.item.instance as ScriptInstance
      const hasInstance = !!instance.id
      if (hasInstance) {
        init.instanceId = instance.id
      }
      const typeStats = this.test.stats[type]
      switch (column.name) {
        case 'steps':
        case 'pass':
        case 'target':
        case 'duration':
          init.value = typeStats[column.name]
          break
        case 'conversion':
          init.target = typeStats.target
          init.pass = typeStats.pass
          if (
            typeof init.target === 'number' &&
            typeof init.pass === 'number' &&
            init.pass > 0
          ) {
            init.value =
              new Intl.NumberFormat('ru-RU', {
                maximumFractionDigits: 2,
              }).format((init.target / init.pass) * 100) + '%'
          } else {
            init.value = '0%'
          }
          init.classes = init.value === this.greatestConversion ? '-green' : ''
      }
      return init
    },
    editTest(type: CardVariant): void {
      // TODO: Implement edit test card function
      window.alert('Edit ' + type)
    },
    removeTest(type: CardVariant): void {
      this.$useConfirmDialog([this.$tc('version.removeConfirmOnStep', 1, { variant: type }), {
        type: 'danger',
        okText: this.$tc('common.remove'),
        cancelText: this.$tc('common.cancel'),
      }], () => {
        // TODO: Implement remove test card function
        window.alert('Remove ' + type)
      })
    },
  },
})
